import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../../../../api/crud';
import { SHIPMENTS_TRACKERS_PATH } from '../../config/service';
import { TAKE_BACK_BAG_COLLECTION_URL } from '../../config/externalURL';
import { isMockActive, registerMockGlobals, MOCK_EVENT } from '../../global/PackageTrackingPill/mock';
import useShareCardAsImage from '../../utils/hooks/useShareCardAsImage';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import {
  ROUTE_GIFT_CARDS_V2,
  ROUTE_TAKE_BACK_BAGS,
  ROUTE_WALLET,
} from '../../config/routes';
import { PackageTrackingPill } from '../../global/PackageTrackingPill';
import TCNavPill from '../../global/TCNavPill/TCNavPill';
import Dashboard from '../../global/Dashboard';
import LoadingBar from '../../global/LoadingBar';
import useUserBalance from '../../utils/hooks/useUserBalance';
import { pushPackageTrackerDrawerViewedEvent } from '../../utils/gtm';

import tbbSingleStepperImage from '../../../../images/tbb-single-stepper.png';
import tbbStackStepperImage from '../../../../images/tbb-stack-stepper.png';
import tbbBagTiltedImage from '../../../../images/tbb-bag-tilted.png';
import tbbWingImage from '../../../../images/tbb-wing-left.png';
import qrLabelImage from '../../../../images/qr-label.png';
import printedLabel1 from '../../../../images/printed-label-1.png';
import printedLabel2 from '../../../../images/printed-label-2.png';
import printedLabel3 from '../../../../images/printed-label-3.png';
import sortingConveyorImage from '../../../../images/sorting-conveyor.png';
import starbucksWordmark from '../../../../images/starbucks-wordmark.svg';
import trashieLogoTransparent from '../../../../images/trashie-logo-transparent.svg';
import runConfetti from './confetti';
import './TrackingDetails.scss';

const STAGE_KEYS = [
  'order_received',
  'bag_delivered',
  'carrier_scan_detected',
  'sorting_center_received',
  'sorting_center_completed',
];

const STEP_LABELS = {
  order_received: 'Order Received',
  bag_delivered: 'Delivered to User',
  carrier_scan_detected: 'Carrier Scan Detected',
  sorting_center_received: 'Processing / Sorting',
  sorting_center_completed: 'Completed',
};

const STEP_DESCRIPTIONS = {
  order_received: 'Help is on the way! Clear a 3x3 square in your closet.',
  bag_delivered: "Your 'Clean Closet Kit' is here. Fill it up!",
  carrier_scan_detected: 'Bag detected! Your bag is on its way.',
  sorting_center_received: 'Your items are being sorted. No landfill for you today!',
  sorting_center_completed: 'Success! You diverted waste and saved resources.',
};

// Stage 1 mobile: Bag Ordered
const STAGE1_MOBILE_TITLE = 'Bag Ordered';
const STAGE1_MOBILE_COPY = 'Order received. Expect your Bag in 3-10 days.';
const STAGE1_FOOTER_ARRIVAL = 'Should arrive in 3-10 days.';
const STAGE1_FOOTER_TRACKING = "We don't offer tracking yet (but it's coming soon)";

// Stage 2 mobile: Labels Created
const STAGE2_PROVIDER_DISPLAY_NAMES = {
  EASYPOST: 'EasyPost',
  FEDEX: 'FedEx',
  UPS: 'UPS',
  USPS: 'USPS',
  DHL: 'DHL',
  HAPPY_RETURNS: 'Happy Returns',
};

function normalizeCarrierToken(token) {
  const cleanToken = String(token || '').trim();
  if (!cleanToken) return '';
  const upperToken = cleanToken.toUpperCase();
  if (STAGE2_PROVIDER_DISPLAY_NAMES[upperToken]) return STAGE2_PROVIDER_DISPLAY_NAMES[upperToken];
  if (upperToken.length <= 4) return upperToken;
  return `${upperToken.charAt(0)}${upperToken.slice(1).toLowerCase()}`;
}

function getCarrierDisplayName(rawCarrier) {
  if (typeof rawCarrier !== 'string') return '';
  const cleanCarrier = rawCarrier.trim();
  if (!cleanCarrier) return '';
  // Keep backend-provided display names as-is (e.g. "Happy Returns", "FedEx Office").
  if (
    (cleanCarrier.includes(' ') && !/[_-]/.test(cleanCarrier))
    || (/[a-z]/.test(cleanCarrier) && /[A-Z]/.test(cleanCarrier))
  ) {
    return cleanCarrier;
  }

  if (/[_-]/.test(cleanCarrier)) {
    return cleanCarrier
      .split(/[_-]+/)
      .map(normalizeCarrierToken)
      .filter(Boolean)
      .join(' ');
  }

  return normalizeCarrierToken(cleanCarrier);
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function getShipmentCarrierName(shipment) {
  const shipmentSteps = asArray(shipment?.steps);
  const carrierStep = shipmentSteps.find(s => s?.key === 'carrier_scan_detected');
  const stepData = carrierStep?.data && typeof carrierStep.data === 'object' ? carrierStep.data : {};
  const carrierCandidates = [
    stepData?.shippingProviderName,
    stepData?.shipping_provider_name,
    stepData?.carrier,
    stepData?.carrierName,
    stepData?.carrier_name,
    stepData?.shippingProvider,
    stepData?.shipping_provider,
    shipment?.shippingProviderName,
    shipment?.shipping_provider_name,
    shipment?.carrier,
    shipment?.carrierName,
    shipment?.shippingProvider,
  ];
  const rawCarrier = carrierCandidates.find(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );

  return getCarrierDisplayName(rawCarrier);
}

export function getStage2DropOffCopy(shipment) {
  const carrierName = getShipmentCarrierName(shipment);
  return carrierName ? `Drop off your bags at ${carrierName}` : 'Drop off your bags';
}

// Stage 3 mobile: Gift Card Earned
const STAGE3_MOBILE_TITLE = 'Gift Card Earned!';
const STAGE3_MOBILE_COPY = 'Donation received. Have a coffee on us!';

// Stage 4 mobile: Your Stuff is Being Sorted (same layout as stage 1)
const STAGE4_MOBILE_TITLE = 'Your Stuff is Being Sorted';
const STAGE4_MOBILE_COPY = "We're routing your stuff for it's next highest use.";

// Stage 5 mobile: Big Win + My Impact
const STAGE5_MOBILE_TITLE = 'Big Win!';
const STAGE5_MOBILE_COPY =
  'We received {lbs} from your last shipment. Every bag counts, towards real impact.';
const STAGE5_IMPACT_LABEL = 'My Impact';
const STAGE5_IMPACT_METRICS = [
  { label: 'Landfill Waste Saved', value: '80 lbs', pillClass: 'TrackingDetails__impactPill--blue' },
  { label: 'Co2 Offset', value: '3.5k lbs', pillClass: 'TrackingDetails__impactPill--pink' },
  { label: 'Water Saved', value: '38k ga.', pillClass: 'TrackingDetails__impactPill--purple' },
];

// Lost state
const LOST_TITLE = 'Your bag is lost';
const LOST_SUBTITLE = '(your points are safe)';
const LOST_COPY =
  "Your bag may have hit a detour. Your rewards won't. We're on it — your points will be credited automatically.";

/** Current stage index 0–4 from steps (first incomplete = current). */
function getCurrentStageIndex(steps) {
  if (!Array.isArray(steps) || steps.length === 0) return 0;
  const idx = steps.findIndex(s => s?.completed === false);
  return idx >= 0 ? idx : steps.length - 1;
}

// API step keys (from API) -> our UI step keys (index in STAGE_KEYS)
const API_STEP_KEYS = [
  'bag_registered',
  'carrier_scan_detected',
  'sorting_center_received',
  'sorting_center_completed',
];

/** Build our 5-step array from an API shipment.
 * Step 0 (order_received) is completed when shipment exists. */
function buildStepsFromShipment(shipment) {
  if (!shipment) return [];
  const apiSteps = asArray(shipment.steps);
  const getApiStep = key => apiSteps.find(s => s?.key === key);
  return [
    { key: STAGE_KEYS[0], completed: true, label: STEP_LABELS[STAGE_KEYS[0]] },
    ...API_STEP_KEYS.map((apiKey, i) => {
      const step = getApiStep(apiKey);
      const ourKey = STAGE_KEYS[i + 1];
      return {
        key: ourKey,
        completed: !!step?.completed,
        completedAt: step?.completedAt ?? null,
        label: STEP_LABELS[ourKey],
        data: step?.data,
      };
    }),
  ];
}

/** Format shipment date for label line e.g. "2 Pack • 03/03/26". */
function formatLabelDate(shipment) {
  const bagCount = shipment?.items?.length ?? 1;
  const d = shipment?.date;
  if (!d) return `${bagCount} Pack`;
  const date = new Date(d);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${bagCount} Pack • ${mm}/${dd}/${yy}`;
}

/** Format weight for display e.g. "2.5 lbs". */
function formatWeight(totalWeightReceived) {
  if (totalWeightReceived?.data == null) return '';
  const value = Number(totalWeightReceived.data);
  const unit = totalWeightReceived.unit === 'lbs' ? 'lbs' : totalWeightReceived.unit || '';
  return `${value} ${unit}`;
}

/** Build impact metrics for stage 5 from API sustainabilityMetrics. */
function getImpactMetricsFromApi(sustainabilityMetrics) {
  if (!sustainabilityMetrics) return null;
  const map = {
    landfillWasteSaved: 'TrackingDetails__impactPill--blue',
    co2Offset: 'TrackingDetails__impactPill--pink',
    waterSaved: 'TrackingDetails__impactPill--purple',
  };
  const order = ['landfillWasteSaved', 'co2Offset', 'waterSaved'];
  return order
    .filter(k => sustainabilityMetrics[k])
    .map(k => {
      const m = sustainabilityMetrics[k];
      const num = m?.data != null ? Number(m.data) : NaN;
      let value = '';
      if (!Number.isNaN(num)) {
        if (m?.unit === 'kg') value = `${num} lbs`;
        if (m?.unit === 'kg') value = `${Math.round(num * 2.20462)} lbs`;
        else if (m?.unit === 'gal') value = num >= 1000 ? `${(num / 1000).toFixed(0)}k ga.` : `${num} ga.`;
        else if (m?.unit === 'lbs') value = num >= 1000 ? `${(num / 1000).toFixed(1)}k lbs` : `${num} lbs`;
        else value = `${num} ${m?.unit || ''}`.trim();
      }
      return { label: m?.label || k, value, pillClass: map[k] || '' };
    });
}

/** Mock trackers with 6 orders, each at a different stage (1–5 + lost). */
function getMockTrackersWithSixOrders() {
  const baseDate = new Date('2026-03-01T10:00:00.000Z');
  const stages = [
    { completedCount: 1, itemsCount: 1 }, // Stage 1: Bag Ordered
    { completedCount: 2, itemsCount: 1 }, // Stage 2: 1 label
    { completedCount: 2, itemsCount: 2, isQR: false }, // Stage 2: 2 printed labels
    { completedCount: 2, itemsCount: 3 }, // Stage 2: 3 labels
    { completedCount: 2, itemsCount: 4 }, // Stage 2: 4 labels (capped at 3 visually)
    { completedCount: 3, itemsCount: 2 }, // Stage 3: Gift Card Earned
    { completedCount: 4, itemsCount: 1 }, // Stage 4: Being Sorted
    { completedCount: 5, itemsCount: 3 }, // Stage 5: Completed
    { lost: true, itemsCount: 2 }, // Lost
  ];
  const shipments = stages.map((stage, idx) => {
    const n = stage.lost ? 3 : stage.completedCount - 1; // API steps completed (0–4)
    const steps = stage.lost
      ? [
        { key: 'bag_registered', completed: true, completedAt: baseDate.toISOString() },
        {
          key: 'carrier_scan_detected',
          completed: true,
          completedAt: baseDate.toISOString(),
          data: { amountEarned: 5 },
        },
        {
          key: 'sorting_center_received',
          completed: true,
          completedAt: baseDate.toISOString(),
          data: { isLost: true },
        },
        { key: 'sorting_center_completed', completed: false, completedAt: null },
      ]
      : API_STEP_KEYS.map((key, i) => {
        let data;
        if (key === 'carrier_scan_detected') {
          data = { hasPassedWeightValidation: true, amountEarned: 5.0 };
        } else if (key === 'sorting_center_completed') {
          data = {
            totalWeightReceived: { data: 15, unit: 'lbs' },
            sustainabilityMetrics: {
              landfillWasteSaved: { data: 80, unit: 'kg', label: 'Landfill Waste Saved' },
              co2Offset: { data: 3500, unit: 'lbs', label: 'CO2 Offset' },
              waterSaved: { data: 38000, unit: 'gal', label: 'Water Saved' },
            },
          };
        }
        return {
          key,
          completed: i < n,
          completedAt: i < n ? baseDate.toISOString() : null,
          data,
        };
      });
    const date = new Date(baseDate);
    date.setDate(date.getDate() + idx);
    return {
      batchId: `mock-batch-${idx + 1}`,
      shippingProviderId: stage.isQR === false ? 'EASYPOST' : 'FEDEX',
      isQR: stage.isQR !== false,
      items: Array.from({ length: stage.itemsCount }, (_, i) => ({
        reference: `000000378919${idx}${i}`,
        type: 'TEXTILE_TBB',
        shipmentUid: `mock-${idx}-${i}`,
      })),
      steps,
      date: date.toISOString(),
    };
  });
  return { shipments, orders: [] };
}

const TrackingDetails = ({ isDrawer = false }) => {
  const { isSignedIn, userData } = useFirebaseAuth();
  const balance = useUserBalance(isSignedIn, userData);
  const [trackers, setTrackers] = useState({ shipments: [], orders: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuDropdownOpen, setMenuDropdownOpen] = useState(false);
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState(0);
  const menuDropdownRef = useRef(null);
  const confettiFiredForStages = useRef(new Set());
  const lastTrackedPackageDrawerViewRef = useRef('');
  const impactCardRef = useRef(null);
  const { share: handleShareImpact, isSharing: isSharingImpact } = useShareCardAsImage({
    cardRef: impactCardRef,
    shareButtonSelector: '.TrackingDetails__impactCardShare',
  });

  useEffect(() => {
    if (!menuDropdownOpen) return undefined;
    const handleClickOutside = (e) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(e.target)) {
        setMenuDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuDropdownOpen]);

  useEffect(() => {
    const len = trackers.shipments?.length ?? 0;
    if (len > 0 && selectedShipmentIndex >= len) setSelectedShipmentIndex(0);
  }, [trackers.shipments?.length, selectedShipmentIndex]);

  const setSelectedOrder = (index) => {
    setSelectedShipmentIndex(index);
    setMenuDropdownOpen(false);
  };

  const fetchTrackers = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (isMockActive()) {
      setTrackers(getMockTrackersWithSixOrders());
      setLoading(false);
      return;
    }
    try {
      const result = await apiFetch({ path: SHIPMENTS_TRACKERS_PATH });
      const raw = result?.data?.trackers ?? result?.trackers ?? result?.data ?? result;
      const shipments = Array.isArray(raw?.shipments) ? raw.shipments : [];
      const orders = Array.isArray(raw?.orders) ? raw.orders : [];
      setTrackers({ shipments, orders });
    } catch (err) {
      setError(err);
      setTrackers({ shipments: [], orders: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    registerMockGlobals();
    fetchTrackers();
  }, [fetchTrackers]);

  useEffect(() => {
    const onMockChange = () => fetchTrackers();
    window.addEventListener(MOCK_EVENT, onMockChange);
    return () => window.removeEventListener(MOCK_EVENT, onMockChange);
  }, [fetchTrackers]);

  useEffect(() => {
    if (!isDrawer) {
      return;
    }
    setSelectedShipmentIndex(0);
  }, [isDrawer]);

  const drawerPillsHeader = isDrawer && userData ? (
    <div className="TrackingDetails__drawerPillsHeader">
      <TCNavPill
        balance={balance !== null ? balance : userData.balance?.currentBalance}
      />
      <PackageTrackingPill selectedShipmentIndex={selectedShipmentIndex} />
    </div>
  ) : null;

  const effectiveTrackers = trackers;
  const shipmentsList = effectiveTrackers.shipments ?? [];
  const clampedIndex = Math.min(selectedShipmentIndex, Math.max(0, shipmentsList.length - 1));
  const primaryShipment = shipmentsList[clampedIndex] ?? shipmentsList[0] ?? null;
  const effectiveSteps = primaryShipment ? buildStepsFromShipment(primaryShipment) : [];
  const effectiveCurrentStageIndex = getCurrentStageIndex(effectiveSteps);
  const allStepsComplete = effectiveSteps.length === 5 && effectiveSteps[4]?.completed;
  const completedCount = allStepsComplete ? 5 : effectiveCurrentStageIndex;

  const bagCount = primaryShipment?.items?.length ?? 1;
  const labelCardCount = Math.min(bagCount, 3);
  const shipmentIsQR = primaryShipment?.isQR !== false;
  const primaryShipmentSteps = asArray(primaryShipment?.steps);

  const carrierStep = primaryShipmentSteps.find(s => s?.key === 'carrier_scan_detected');
  const sortingCompletedStep = primaryShipmentSteps.find(s => s?.key === 'sorting_center_completed');
  const labelDateStr = formatLabelDate(primaryShipment);
  const giftCardAmount =
    carrierStep?.data?.amountEarned != null ? carrierStep.data.amountEarned : 5;
  const totalWeightStr = sortingCompletedStep?.data?.totalWeightReceived
    ? formatWeight(sortingCompletedStep.data.totalWeightReceived)
    : '15 lbs';
  const impactMetricsFromApi = getImpactMetricsFromApi(
    sortingCompletedStep?.data?.sustainabilityMetrics,
  );
  const isLost = primaryShipmentSteps.some(
    s => s?.key === 'sorting_center_received' && s?.data?.isLost === true,
  );

  useEffect(() => {
    if (!isDrawer || loading) {
      return;
    }

    const stage = isLost ? 'lost' : completedCount || 0;
    const trackingKey = `${clampedIndex}-${stage}`;
    if (lastTrackedPackageDrawerViewRef.current === trackingKey) {
      return;
    }

    lastTrackedPackageDrawerViewRef.current = trackingKey;
    pushPackageTrackerDrawerViewedEvent({ stage });
  }, [isDrawer, loading, isLost, completedCount, clampedIndex]);

  useEffect(() => {
    const shouldFire = (completedCount === 3 || completedCount === 5)
      && !confettiFiredForStages.current.has(completedCount);
    if (shouldFire) {
      confettiFiredForStages.current.add(completedCount);
      runConfetti();
    }
  }, [completedCount]);

  if (loading) {
    return (
      <Dashboard className={isDrawer ? 'Dashboard--trackingDrawer' : ''}>
        {drawerPillsHeader}
        <LoadingBar />
      </Dashboard>
    );
  }

  const menuDropdownPanel = menuDropdownOpen && (
    <div className="TrackingDetails__menuDropdown">
      {shipmentsList.length > 0 && (
        <>
          <div className="TrackingDetails__menuDropdownTitle">
            <span>Orders</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M4 10L8 6L12 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          {shipmentsList.map((s, i) => {
            const itemCount = s?.items?.length ?? 0;
            const bagsLabel = itemCount === 1 ? '1 Bag' : `${itemCount} Bags`;
            const dateStr = s?.date
              ? new Date(s.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
              : '';
            return (
              <button
                key={s?.batchId ?? i}
                type="button"
                className={`TrackingDetails__menuDropdownItem ${
                  i === clampedIndex ? 'TrackingDetails__menuDropdownItem--active' : ''
                }`}
                onClick={() => setSelectedOrder(i)}
              >
                <span className="TrackingDetails__menuDropdownBags">{bagsLabel}</span>
                {dateStr ? (
                  <span className="TrackingDetails__menuDropdownDate">
                    On
                    {' '}
                    {dateStr}
                  </span>
                ) : null}
              </button>
            );
          })}
        </>
      )}
    </div>
  );

  const hasNoData = !trackers.shipments?.length && !trackers.orders?.length;
  if (error || hasNoData) {
    return (
      <Dashboard className={isDrawer ? 'Dashboard--trackingDrawer' : ''}>
        {drawerPillsHeader}
        <div className="TrackingDetails TrackingDetails--mobile TrackingDetails--stage1">
          <div className="TrackingDetails__card">
            <div className="TrackingDetails__header">
              <h2 className="TrackingDetails__trackingTitle">Trashie Tracker</h2>
            </div>
            <div className="TrackingDetails__progressBar">
              <div
                className="TrackingDetails__progressBarFill"
                style={{ '--fill-ratio': 0.2 }}
              />
              <div
                className="TrackingDetails__progressBarBag"
                style={{ '--dot-index': 0 }}
              >
                <img src={tbbSingleStepperImage} alt="" aria-hidden />
              </div>
              <div className="TrackingDetails__progressBarMarkers">
                {STAGE_KEYS.map((stageKey, i) => (
                  <span
                    key={stageKey}
                    className={`TrackingDetails__progressBarMarker ${
                      i === 0 ? 'TrackingDetails__progressBarMarker--active' : ''
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="TrackingDetails__wingedBag">
              <img className="TrackingDetails__wingLeft" src={tbbWingImage} alt="" aria-hidden />
              <img className="TrackingDetails__bagTilted" src={tbbBagTiltedImage} alt="Take Back Bag" />
              <img className="TrackingDetails__wingRight" src={tbbWingImage} alt="" aria-hidden />
            </div>
            <p className="TrackingDetails__stageCopy">
              No active shipments yet. When you order a Take Back Bag, it will
              be lovingly prepared at our distribution facility.
            </p>
            <a
              href={TAKE_BACK_BAG_COLLECTION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="TrackingDetails__cta"
            >
              Find Drop Off
            </a>
          </div>
        </div>
      </Dashboard>
    );
  }

  const showMobileCard = effectiveSteps.length > 0;
  if (showMobileCard) {
    const isNoShipments = effectiveCurrentStageIndex === 0;
    const isStage1 = completedCount === 1; // one circle = first step done
    const isStage2 = completedCount === 2; // Declutter Prep
    const isStage3 = completedCount === 3; // Bag Shipped
    const isStage4 = completedCount === 4; // Sorting Bag
    const isStage5 = completedCount === 5; // Congratulations
    const isComplete = completedCount === 5;
    return (
      <Dashboard className={isDrawer ? 'Dashboard--trackingDrawer' : ''}>
        {drawerPillsHeader}
        <div
          className={`TrackingDetails TrackingDetails--mobile ${
            isStage1 ? 'TrackingDetails--stage1' : ''
          } ${isStage2 ? 'TrackingDetails--stage2' : ''} ${isStage3 ? 'TrackingDetails--stage3' : ''} ${
            isStage4 ? 'TrackingDetails--stage4' : ''
          } ${isStage5 ? 'TrackingDetails--stage5' : ''} ${
            isNoShipments ? 'TrackingDetails--noBar' : ''
          } ${isLost ? 'TrackingDetails--lost' : ''}`}
        >
          <div className="TrackingDetails__card">
            {!isNoShipments && (
            <>
              <div className="TrackingDetails__header" ref={menuDropdownRef}>
                <h2 className="TrackingDetails__trackingTitle">Trashie Tracker</h2>
                <div className="TrackingDetails__menuButtonWrap">
                  <button
                    type="button"
                    className="TrackingDetails__menuButton"
                    aria-label="Menu"
                    aria-expanded={menuDropdownOpen}
                    onClick={(e) => { e.stopPropagation(); setMenuDropdownOpen(prev => !prev); }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <circle cx="12" cy="6" r="1.5" fill="currentColor" />
                      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                      <circle cx="12" cy="18" r="1.5" fill="currentColor" />
                    </svg>
                  </button>
                  {menuDropdownPanel}
                </div>
              </div>
              <div
                className={`TrackingDetails__progressBar ${
                  isComplete ? 'TrackingDetails__progressBar--complete' : ''
                } ${isLost ? 'TrackingDetails__progressBar--lost' : ''}`}
              >
                <div
                  className="TrackingDetails__progressBarFill"
                  style={{ '--fill-ratio': Math.min((completedCount + 0.2) / 5, 1) }}
                />
                {!isLost && (
                  <div
                    className="TrackingDetails__progressBarBag"
                    style={{ '--dot-index': completedCount - 1 }}
                  >
                    <img
                      src={bagCount > 1 ? tbbStackStepperImage : tbbSingleStepperImage}
                      alt=""
                      aria-hidden
                    />
                  </div>
                )}
                <div className="TrackingDetails__progressBarMarkers">
                  {STAGE_KEYS.map((stageKey, i) => {
                    if (isLost && i === 3) {
                      return (
                        <span
                          key={`${stageKey}-warning`}
                          className="TrackingDetails__progressBarMarkerWarning"
                          aria-hidden
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M21.7299 18L13.7299 3.99998C13.5555 3.69218 13.3025 3.43617 12.9969 3.25805C12.6912 3.07993 12.3437 2.98608 11.9899 2.98608C11.6361 2.98608 11.2887 3.07993 10.983 3.25805C10.6773 3.43617 10.4244 3.69218 10.2499 3.99998L2.24993 18C2.07361 18.3053 1.98116 18.6519 1.98194 19.0045C1.98272 19.3571 2.07671 19.7032 2.25438 20.0078C2.43204 20.3124 2.68708 20.5646 2.99362 20.7388C3.30017 20.9131 3.64734 21.0032 3.99993 21H19.9999C20.3508 20.9996 20.6955 20.9069 20.9992 20.7313C21.303 20.5556 21.5551 20.3031 21.7304 19.9991C21.9057 19.6951 21.998 19.3504 21.9979 18.9995C21.9978 18.6486 21.9054 18.3039 21.7299 18Z" stroke="#333333" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 9V13" stroke="#333333" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 17H12.01" stroke="#333333" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      );
                    }
                    return (
                      <span
                        key={stageKey}
                        className={`TrackingDetails__progressBarMarker ${
                          i < completedCount ? 'TrackingDetails__progressBarMarker--active' : ''
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
              {!isStage3 && (
                <p className="TrackingDetails__labelDate">{labelDateStr || '1 Pack'}</p>
              )}
            </>
            )}
            {isNoShipments && (
            <>
              <h1 className="TrackingDetails__title">Track your bag</h1>
              <p className="TrackingDetails__stageCopy">
                No active shipments yet. When you order a Take Back Bag, it will
                be lovingly prepared at our distribution facility.
              </p>
              <a
                href={TAKE_BACK_BAG_COLLECTION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="TrackingDetails__cta"
              >
                Find drop off
              </a>
              <p className="TrackingDetails__footer">
                {STAGE1_FOOTER_ARRIVAL}
                <br />
                {STAGE1_FOOTER_TRACKING}
              </p>
            </>
            )}
            {isStage1 && !isNoShipments && !isLost && (
            <>
              <h1 className="TrackingDetails__title">{STAGE1_MOBILE_TITLE}</h1>
              <p className="TrackingDetails__stageCopy TrackingDetails__stageCopy--stage1">
                {STAGE1_MOBILE_COPY}
              </p>
              <div className="TrackingDetails__wingedBag">
                <img className="TrackingDetails__wingLeft" src={tbbWingImage} alt="" aria-hidden />
                <img className="TrackingDetails__bagTilted" src={tbbBagTiltedImage} alt="Take Back Bag" />
                <img className="TrackingDetails__wingRight" src={tbbWingImage} alt="" aria-hidden />
              </div>
            </>
            )}
            {isStage2 && !isNoShipments && !isLost && (
            <>
              <h1 className="TrackingDetails__title">
                {bagCount === 1
                  ? '1 Label Created!'
                  : `${bagCount} Labels Created!`}
              </h1>
              <p className="TrackingDetails__stageCopy TrackingDetails__stageCopy--stage2">
                {getStage2DropOffCopy(primaryShipment)}
              </p>
              <div className="TrackingDetails__stage2LabelCards">
                {shipmentIsQR ? (
                  Array.from({ length: labelCardCount }, (_, i) => {
                    const isLast = i === labelCardCount - 1;
                    return (
                      <div
                        key={`label-${i}`}
                        className={`TrackingDetails__stage2LabelCard${
                          i > 0 ? ' TrackingDetails__stage2LabelCard--overlap' : ''
                        }`}
                      >
                        <img src={qrLabelImage} alt="Shipping label" />
                        {isLast && (
                          <span className="TrackingDetails__stage2LabelCheck" aria-hidden>
                            <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="13.5" cy="13.5" r="13.5" fill="#2B8747" />
                              <path d="M8 13.5l3.5 3.5L19 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                        )}
                      </div>
                    );
                  })
                ) : (
                  (() => {
                    let labelSrc = printedLabel1;
                    if (labelCardCount >= 3) labelSrc = printedLabel3;
                    else if (labelCardCount === 2) labelSrc = printedLabel2;
                    return (
                      <img
                        className="TrackingDetails__stage2PrintedLabels"
                        src={labelSrc}
                        alt="Shipping labels"
                      />
                    );
                  })()
                )}
              </div>
              <Link
                to={ROUTE_TAKE_BACK_BAGS}
                className="TrackingDetails__cta TrackingDetails__cta--stage2Labels"
              >
                View My Shipping Labels
              </Link>
            </>
            )}
            {isStage3 && !isNoShipments && !isLost && (
            <>
              <p className="TrackingDetails__labelDate">{labelDateStr}</p>
              <h1 className="TrackingDetails__title">{STAGE3_MOBILE_TITLE}</h1>
              <p className="TrackingDetails__stageCopy">
                {STAGE3_MOBILE_COPY}
              </p>
              <div className="TrackingDetails__giftCard">
                <div className="TrackingDetails__giftCardBottom">
                  <img
                    className="TrackingDetails__giftCardBrand"
                    src={starbucksWordmark}
                    alt="Starbucks"
                  />
                  <span className="TrackingDetails__giftCardValue">
                    $
                    {Number(giftCardAmount)}
                  </span>
                </div>
              </div>
              <Link
                to={ROUTE_GIFT_CARDS_V2}
                className="TrackingDetails__cta TrackingDetails__cta--pill"
              >
                Explore All Gift Cards
              </Link>
              <p className="TrackingDetails__orDivider">or</p>
              <Link
                to={ROUTE_WALLET}
                className="TrackingDetails__ctaLink"
              >
                Bank it for later
              </Link>
            </>
            )}
            {isStage4 && !isNoShipments && !isLost && (
            <>
              <h1 className="TrackingDetails__title TrackingDetails__title--stage4">{STAGE4_MOBILE_TITLE}</h1>
              <p className="TrackingDetails__stageCopy TrackingDetails__stageCopy--stage4">
                {STAGE4_MOBILE_COPY}
              </p>
              <div className="TrackingDetails__stage1Image">
                <img src={sortingConveyorImage} alt="Items being sorted" />
              </div>
            </>
            )}
            {isStage5 && !isNoShipments && !isLost && (
            <>
              <h1 className="TrackingDetails__title">{STAGE5_MOBILE_TITLE}</h1>
              <p className="TrackingDetails__stageCopy TrackingDetails__stageCopy--stage5">
                {STAGE5_MOBILE_COPY.replace('{lbs}', totalWeightStr)}
              </p>
              <div className="TrackingDetails__impactCard" ref={impactCardRef}>
                <div className="TrackingDetails__impactCardHeader">
                  <h2 className="TrackingDetails__impactCardTitle">{STAGE5_IMPACT_LABEL}</h2>
                  <button
                    type="button"
                    className="TrackingDetails__impactCardShare"
                    aria-label="Share"
                    onClick={handleShareImpact}
                    disabled={isSharingImpact}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="16,6 12,2 8,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <line x1="12" y1="2" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                <div className="TrackingDetails__impactCardMetrics">
                  {(impactMetricsFromApi && impactMetricsFromApi.length > 0
                    ? impactMetricsFromApi
                    : STAGE5_IMPACT_METRICS
                  ).map(({ label, value, pillClass }) => (
                    <div key={label} className="TrackingDetails__impactMetric">
                      <span className="TrackingDetails__impactMetricLabel">{label}</span>
                      <span className={`TrackingDetails__impactPill ${pillClass}`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="TrackingDetails__impactCardLogo">
                  <img src={trashieLogoTransparent} alt="Trashie" />
                </div>
              </div>
            </>
            )}
            {isLost && (
            <>
              <h1 className="TrackingDetails__title">{LOST_TITLE}</h1>
              <p className="TrackingDetails__lostSubtitle">{LOST_SUBTITLE}</p>
              <p className="TrackingDetails__stageCopy TrackingDetails__stageCopy--lost">
                {LOST_COPY}
              </p>
            </>
            )}
            {!isStage1 && !isStage2 && !isStage3 && !isStage4 && !isStage5
            && !isNoShipments && !isLost && (
            <>
              <h1 className="TrackingDetails__title">Track your bag</h1>
              <p className="TrackingDetails__stageCopy">
                {STEP_DESCRIPTIONS[STAGE_KEYS[completedCount - 1]]}
              </p>
            </>
            )}
          </div>
        </div>
      </Dashboard>
    );
  }

  return null;
};

export default TrackingDetails;
